// Прагът „сервира след" по станциите на реда и таванът му — огледало на BE
// monitors/serving.py (`ServeRule.serve_from_status_of_row`). Прагът на реда е
// най-разрешаващият сред станциите му и на добавките му, които имат
// сервитьорски монитор (без такава — без ограничение). Ред стига най-много до
// статуса, който монитор на заведението пише за станциите му: кухненски →
// „Сготвено", шпикер → „Издадено", само принтер → „Незапочнато" (никой не го
// мени).
// „Сервира след" (и „вижда след" на сервитьорския екран) на реда не е след
// това: иначе ред на станция, която шпикерът не слуша, не би се сервирал
// никога. Общо за сервитьорския екран (screenRules.ts) и Маркиране
// (ordering/.../serveRule.ts).
import type {
  KitchenStatus,
  ReachableStatusByStation,
  ServeFromStatusByStation,
} from "../../api/monitors";
import { isKitchenStatus } from "./visibleFromStatus";

const NOT_STARTED: KitchenStatus = "0";

// Докъде може да стигне ред на тези станции — най-далечната от тях; станция
// без монитор (и ред без станции) — „Незапочнато". null — картата липсва
// (стар BE, огледало на касата, грешка): без таван.
export const reachableStatusOf = (
  stationIds: readonly number[],
  reachableStatusByStation: ReachableStatusByStation | null | undefined,
): KitchenStatus | null => {
  if (!reachableStatusByStation) return null;
  return stationIds.reduce<KitchenStatus>((farthest, stationId) => {
    const reachable = reachableStatusByStation[String(stationId)];
    return isKitchenStatus(reachable) && reachable > farthest
      ? reachable
      : farthest;
  }, NOT_STARTED);
};

// Прагът, но не след статуса, до който редът изобщо може да стигне.
export const capByReachableStatus = (
  threshold: KitchenStatus,
  reachable: KitchenStatus | null,
): KitchenStatus =>
  reachable != null && reachable < threshold ? reachable : threshold;

// Прагът на ред на тези станции — най-разрешаващият (най-ранният) сред
// станциите със сервитьорски монитор; null — никоя няма (без ограничение).
export const serveFromStatusOfStations = (
  stationIds: readonly number[],
  serveFromStatusByStation: ServeFromStatusByStation,
): KitchenStatus | null =>
  stationIds.reduce<KitchenStatus | null>((earliest, stationId) => {
    const threshold = serveFromStatusByStation[String(stationId)];
    if (!isKitchenStatus(threshold)) return earliest;
    return earliest == null || threshold < earliest ? threshold : earliest;
  }, null);

// Станциите, на които мониторите показват реда (BE `row_station_ids`): всички
// части на ред на няколко станции (и `tracked: false`), иначе основната.
export const rowStationIdsOf = (
  parts: ReadonlyArray<{ stationId: number }> | null | undefined,
  singleStationId: number | null | undefined,
): number[] => {
  if (parts && parts.length >= 2) return parts.map((part) => part.stationId);
  return singleStationId != null ? [singleStationId] : [];
};

// Станциите, чиито части движат обобщения статус на ред (без частите
// `tracked: false`, както BE `status_station_ids`); ред на една станция —
// основната.
export const statusStationIdsOf = (
  parts: ReadonlyArray<{ stationId: number; tracked?: boolean }> | null | undefined,
  singleStationId: number | null | undefined,
): number[] => {
  if (parts && parts.length >= 2) {
    const counted = parts.filter((part) => part.tracked !== false);
    return (counted.length > 0 ? counted : parts).map((part) => part.stationId);
  }
  return singleStationId != null ? [singleStationId] : [];
};
